/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import * as commonConstants from '../../../../common/constants';
import envConfig from '../../../../common/env';
import utils from '../../../../common/utils';

/** Same rules as GetTenantConfigService: merge current-env + Stack B tenant ids when crossing env via ?env= */
function shouldMergeStackBTenants(isStackB, env) {
  const currentEnv = envConfig.ENVIRONMENT;
  return (
    isStackB &&
    (env === commonConstants.ENV_PROD || (env === commonConstants.ENV_STG && currentEnv !== commonConstants.ENV_STG))
  );
}

export default class TenantsService extends BaseService {
  constructor() {
    super(constants.GET_TENANT_LIST, 'tenantListStatus');
  }

  async handleEvent() {
    const { isStackB, env } = utils.getStackBParams();

    if (shouldMergeStackBTenants(isStackB, env)) {
      const enhancedHeaders = await utils.resolveStackBHeaders({});
      const [currentResponse, stackBResponse] = await Promise.allSettled([
        this.getClient('aiadminv2').get('/tenants'),
        this.getClient('aiadminstackbv2').get('/tenants', { headers: enhancedHeaders })
      ]);

      if (currentResponse.status === 'rejected') {
        throw currentResponse.reason;
      }

      const currentTenants = currentResponse.value?.data?.data || [];

      if (stackBResponse.status === 'rejected') {
        Framework.getStoreRegistry().getStore('notificationState').set({
          open: true,
          message: 'Error fetching Stack B tenants. Showing current environment tenants.',
          type: 'error'
        });
        return currentResponse.value;
      }

      const stackBTenants = stackBResponse.value?.data?.data || [];
      const stackBTenantsByName = new Map(stackBTenants.map((st) => [st.name, st]));
      const currentTenantNames = new Set(currentTenants.map((ct) => ct.name));

      const mergedTenants = currentTenants
        .filter((ct) => stackBTenantsByName.has(ct.name))
        .map((ct) => ({
          ...ct,
          tenantId: stackBTenantsByName.get(ct.name).tenantId
        }));

      const stackBOnlyTenants = stackBTenants.filter((st) => !currentTenantNames.has(st.name));

      return { data: { data: [...mergedTenants, ...stackBOnlyTenants] } };
    }

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders({});
      try {
        return await this.getClient('aiadminstackbv2').get('/tenants', { headers: enhancedHeaders });
      } catch (err) {
        Framework.getStoreRegistry().getStore('notificationState').set({
          open: true,
          message: 'Error fetching Stack B tenants. Showing current environment tenants.',
          type: 'error'
        });
        return this.getClient('aiadminv2').get('/tenants');
      }
    }

    return this.getClient('aiadminv2').get('/tenants');
  }

  handleResponse(response) {
    // eslint-disable-line class-methods-use-this
    Framework.getStoreRegistry().getStore('tenantState').setTenantsList(response?.data?.data);
  }

  handleError(error) {
    // eslint-disable-line class-methods-use-this
    const { response } = error;
    if (response) {
      const { status } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
    }
    const notification = {
      open: true,
      message: 'Error fetching tenant list',
      type: 'error'
    };
    Framework.getStoreRegistry().getStore('notificationState').set(notification);
  }
}
